import React, { useEffect, useState, useCallback } from 'react'
import { MiniAppText } from '@/ui/molecules/mini-app-text'
import { MiniAppProps } from './types'
import { User } from '@sourcepilot/generated/base/api/user'
import { useSimpleAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth'
import { useImpersonation } from '@sourcepilot/ui/shell/auth/use-impersonation'
import { UserCheck, Shield } from 'lucide-react'
import { QueryNames } from '@sourcepilot/generated/query-names';

interface UserItem {
  id: string
  name: string
  avatar: string
}

function UserListItem({
  user,
  containerWidth,
  onImpersonate,
  isCurrentUser,
}: {
  user: UserItem
  containerWidth: number
  /** by User node id: the sign-in identifier is not public (VC-Authz 9db79b501) */
  onImpersonate: (userId: string) => void
  isCurrentUser: boolean
}) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <li
      className="p-auto m-auto leading-none pl-2 py-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-2 group">
        {/* Avatar */}
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="w-6 h-6 rounded-full flex-shrink-0"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
            <span className="text-xs text-gray-600">
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        )}

        {/* User info */}
        <div className="flex-1 min-w-0">
          <MiniAppText
            className="text-sm truncate"
            containerWidth={containerWidth - 80}
            isActive={isCurrentUser}
            isHovered={isHovered}
          >
            {user.name || 'Unnamed user'}
          </MiniAppText>
        </div>

        {/* Impersonate button - only show on hover for non-current users */}
        {!isCurrentUser && (
          <button
            onClick={() => onImpersonate(user.id)}
            className="p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200 rounded"
            title={`View as ${user.name || 'this user'}`}
          >
            <UserCheck className="h-4 w-4 text-orange-500" />
          </button>
        )}

        {/* Current user indicator */}
        {isCurrentUser && (
          <span className="text-xs text-green-600 font-medium">(you)</span>
        )}
      </div>
    </li>
  )
}

export function AdminMiniApp({ containerWidth }: MiniAppProps) {
  const { user: currentUser } = useSimpleAuth()
  const { startImpersonation, isAdmin } = useImpersonation()

  const [users, setUsers] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return

    setLoading(true)
    setError(null)

    try {
      // Use the generated User query - security filters will be applied server-side
      // Admin in admin_mode will see all users, non-admin will see only themselves.
      // User discovery presents id, name and avatar only (VC-Authz A6): a
      // user's organisations come from an organisation's roster, so the
      // list shows no organisation.
      const results = await User.setQuery(QueryNames.baseAllUsers).all()

      setUsers(results.map(u => ({
        id: u.id || '',
        name: u.name || '',
        avatar: u.avatar || '',
      })))
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleImpersonate = async (userId: string) => {
    const success = await startImpersonation(userId)
    if (success) {
      // Page will reload after impersonation starts
    }
  }

  // Only show for admins
  if (!isAdmin) {
    return (
      <div className="pl-[4px]">
        <div className="flex items-center justify-between px-2 py-1">
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight my-1">Admin</h4>
        </div>
        <div className="px-2 py-2 text-sm text-gray-500 italic">
          Admin access required
        </div>
      </div>
    )
  }

  return (
    <div className="pl-[4px]">
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-500" />
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight my-1">Admin</h4>
        </div>
      </div>

      {/* Users section */}
      <div className="px-2 py-1">
        <h5 className="text-sm font-medium text-gray-600 mb-1">Users</h5>
      </div>

      <div className="border-none">
        {loading && users.length === 0 ? (
          <div className="px-2 py-2 text-sm text-gray-500 italic">
            Loading users...
          </div>
        ) : error ? (
          <div className="px-2 py-2 text-sm text-red-600">
            Error: {error}
          </div>
        ) : users.length > 0 ? (
          <ul className="p-0 list-none">
            {users.map((user) => (
              <UserListItem
                key={user.id}
                user={user}
                containerWidth={containerWidth}
                onImpersonate={handleImpersonate}
                isCurrentUser={!!user.id && user.id === currentUser?.user_node_id}
              />
            ))}
          </ul>
        ) : (
          <div className="px-2 py-2 text-sm text-gray-500 italic">
            No users found
          </div>
        )}
      </div>
    </div>
  )
}
